import { secureStorage } from './secureStorage';
import dayjs from 'dayjs';

/**
 * Checks if a user has valid step-up access to an organization
 * @param organizationId The ID of the organization to check access for
 * @returns boolean indicating if the user has valid access
 */
export const hasStepUpAccess = (organizationId: string): boolean => {
  try {
    const accessData = secureStorage.getItem('step-up');
    if (!accessData) return false;

    const accessMap = JSON.parse(accessData);
    const expirationDate = accessMap[organizationId];
    
    if (!expirationDate) return false;

    const now = dayjs();
    const expiration = dayjs(expirationDate);
    const isValid = expiration.diff(now, 'minute') > 30;

    // If the access is expired, remove it from storage
    if (!isValid) {
      delete accessMap[organizationId];
      secureStorage.setItem('step-up', JSON.stringify(accessMap));
    }

    return isValid;
  } catch (error) {
    console.error('Error checking step-up access:', error);
    return false;
  }
};

/**
 * Gets all organizations that the user has valid step-up access to
 * @returns Array of organization IDs that have valid access
 */
export const getValidStepUpAccess = (): string[] => {
  try {
    const accessData = secureStorage.getItem('step-up');
    if (!accessData) return [];

    const accessMap = JSON.parse(accessData);
    const now = dayjs();

    return Object.entries(accessMap)
      .filter(([_, expirationDate]) => {
        const expiration = dayjs(expirationDate as string);
        return expiration.diff(now, 'minute') > 30;
      })
      .map(([orgId]) => orgId);
  } catch (error) {
    console.error('Error getting valid step-up access:', error);
    return [];
  }
};

/**
 * Clears step-up access for a specific organization
 * @param organizationId The ID of the organization to clear access for
 */
export const clearStepUpAccess = (organizationId: string): void => {
  try {
    const accessData = secureStorage.getItem('step-up');
    if (!accessData) return;

    const accessMap = JSON.parse(accessData);
    delete accessMap[organizationId];
    secureStorage.setItem('step-up', JSON.stringify(accessMap));
  } catch (error) {
    console.error('Error clearing step-up access:', error);
  }
};

/**
 * Clears all step-up access records
 */
export const clearAllStepUpAccess = (): void => {
  try {
    secureStorage.removeItem('step-up');
  } catch (error) {
    console.error('Error clearing all step-up access:', error);
  }
}; 